import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActionModal } from "@/app/ActionModal";
import { useCanWrite } from "@/app/auth";
import { CAPTURE_STATE, captures, captureTarget, type Capture } from "@/app/captures";
import { EntityDrawer } from "@/app/EntityDrawer";
import { accounts, channels, customers } from "@/app/master";
import { accountOptions, idOptions, loadingOf, useList } from "@/app/queries";
import { Alert, Button, Dropdown, EmptyState, formatTime, PageHeader, Pill, Table, TableWrap, Th } from "@/ui";
import { CaptureForm } from "./CaptureForm";
import { captureDraft, captureInput } from "./captureDraft";

/** 抓包调试（决策 D93）：选一个客户 SMPP 账号或供应商 SMPP 通道，在限定时长与条数内记录会话层收发的每个 PDU，
 * 进详情页按时间线看解码结果，或下载 pcap 用 Wireshark 看。有进行中的任务时每 5 秒刷新条数。 */
export function Captures() {
  const canWrite = useCanWrite(); // 只读账号只看不建，服务端同样拒绝（docs/04 第 9 节）
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [stopping, setStopping] = useState<Capture | null>(null);
  const list = useQuery({ queryKey: ["captures"], queryFn: captures.list, refetchInterval: (q) => (q.state.data?.some((c) => c.state === "running") ? 5_000 : false) });
  const accountRows = useList("accounts", accounts).data;
  const channelRows = useList("channels", channels).data;
  const customerRows = useList("customers", customers).data;
  const target = captureTarget(accountRows, channelRows);
  const rows = list.data ?? [];

  return (
    <>
      <PageHeader title="抓包调试" description="记录客户账号或供应商通道 SMPP 会话上的每个 PDU（含 TLS，密码打码、内容原文）；到期或满条数自动停止，到期 24 小时后清理。"
        actions={canWrite && <Button variant="primary" onClick={() => setCreating(true)}>新建抓包</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <TableWrap loading={loadingOf(list)}>
        {rows.length ? (
          <Table>
            <thead><tr><Th w="id">ID</Th><Th w={240}>对象</Th><Th w="status">状态</Th><Th numeric w={120}>已抓 / 上限</Th><Th w="time">开始</Th><Th w={232}>结束</Th><Th w={120}>创建人</Th><Th w={200}>备注</Th><Th /></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td className="ax-mono">#{c.id}</td><td>{target(c)}</td>
                  <td><Pill tone={CAPTURE_STATE[c.state].tone}>{CAPTURE_STATE[c.state].label}</Pill></td>
                  <td className="ax-num">{c.packets ?? "—"} / {c.max_packets}</td>
                  <td className="ax-mono">{formatTime(c.started_at)}</td>
                  <td className="ax-mono">{formatTime(c.stopped_at ?? c.expires_at)}{c.state === "running" && <span className="ax-secondary">（到期）</span>}</td>
                  <td>{c.created_by}</td><td className="ax-secondary">{c.note}</td>
                  <td>
                    <Dropdown iconOnly label="更多操作" variant="ghost" size="sm" align="end" items={[
                      { key: "view", label: "查看", onSelect: () => navigate(`/captures/${c.id}`) },
                      { key: "pcap", label: "下载 pcap", onSelect: () => window.location.assign(captures.pcapURL(c.id)) },
                      ...(canWrite && c.state === "running" ? ["sep" as const, { key: "stop", label: "停止", onSelect: () => setStopping(c) }] : []),
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !list.isPending && <EmptyState title="没有抓包任务">新建抓包后，已建立的会话立即开始记录，不用客户或上游重新 bind。</EmptyState>
        )}
      </TableWrap>
      {creating && (
        <EntityDrawer title="新建抓包" submitLabel="开始抓包" initial={captureDraft()} convert={captureInput} save={captures.create}
          invalidate={["captures"]} message="已开始抓包，几秒内下发到 SMPP 实例" onClose={() => setCreating(false)}>
          {(f) => <CaptureForm draft={f.draft} errors={f.errors} set={f.set}
            accounts={accountOptions(accountRows?.filter((a) => a.protocol === "smpp"), customerRows)}
            channels={idOptions(channelRows?.filter((ch) => ch.protocol === "smpp"))} />}
        </EntityDrawer>
      )}
      {stopping && <StopModal capture={stopping} name={target(stopping)} onClose={() => setStopping(null)} />}
    </>
  );
}

/** 停止的二次确认：停止后不再记录，已抓到的数据保留到到期后 24 小时。 */
export function StopModal({ capture, name, onClose }: { capture: Capture; name: string; onClose: () => void }) {
  return (
    <ActionModal title={`停止抓包 #${capture.id}`} confirmLabel="停止" action={() => captures.stop(capture.id)} invalidate={["captures"]} message={`已停止抓包 ${name}`} onClose={onClose}
      note={<>{name} 几秒内停止记录，已抓到的 PDU 保留到原定到期后 24 小时。此操作写入审计日志。</>} />
  );
}
